<script setup lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import type {
  DeliveryConnection,
  DeliveryPlatform,
  DeliveryReview,
  DeliveryReviewFilters
} from '@/types/deliveryReviews'
import { DELIVERY_PLATFORM_LABELS } from '@/types/deliveryReviews'
import { fetchReviews } from '../deliveryReviewsApi'
import DeliveryReviewCard from './DeliveryReviewCard.vue'

const props = defineProps<{
  merchantId: number
  merchantStores: { id: number; name: string }[]
  connections: DeliveryConnection[]
}>()

/** reka-ui reserves the empty string, so "all" needs its own value. */
const ALL = 'all'

const { t } = useI18n()

const reviews = ref<DeliveryReview[]>([])
const isLoading = ref(false)
const hasError = ref(false)
const page = ref(1)
const lastPage = ref(1)
const total = ref(0)

const platform = ref<string>(ALL)
const storeId = ref<string>(ALL)
const rating = ref<string>(ALL)
const onlyUnanswered = ref(false)

const connectedPlatforms = computed(() =>
  props.connections
    .filter((connection) => connection.stores.some((store) => store.storeId !== null))
    .map((connection) => connection.platform)
)

const mappedStores = computed(() => {
  const mappedIds = new Set(props.connections.flatMap((connection) => connection.stores.map((store) => store.storeId)))
  return props.merchantStores.filter((store) => mappedIds.has(store.id))
})

const filters = computed<DeliveryReviewFilters>(() => ({
  platform: platform.value === ALL ? null : (platform.value as DeliveryPlatform),
  storeId: storeId.value === ALL ? null : Number(storeId.value),
  rating: rating.value === ALL ? null : Number(rating.value),
  onlyUnanswered: onlyUnanswered.value
}))

/** Id of the latest request: a slower, older response must not overwrite a newer one. */
let lastRequestId = 0

async function load(targetPage = 1) {
  const requestId = ++lastRequestId
  isLoading.value = true
  hasError.value = false
  try {
    const response = await fetchReviews(props.merchantId, filters.value, targetPage)
    if (requestId !== lastRequestId) return
    reviews.value = response.data
    page.value = response.meta.current_page
    lastPage.value = response.meta.last_page
    total.value = response.meta.total
  } catch {
    if (requestId === lastRequestId) hasError.value = true
  } finally {
    if (requestId === lastRequestId) isLoading.value = false
  }
}

function onReplied(updated: DeliveryReview) {
  reviews.value = reviews.value.map((review) => (review.id === updated.id ? updated : review))
}

watch(filters, () => load(1), { deep: true })
onMounted(() => load(1))
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-end gap-3">
      <Select v-if="connectedPlatforms.length > 1" v-model="platform">
        <SelectTrigger class="w-44" :aria-label="t('pages.deliveryReviews.reviews.filters.platform')">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem :value="ALL">{{ t('pages.deliveryReviews.reviews.filters.allPlatforms') }}</SelectItem>
          <SelectItem v-for="slug in connectedPlatforms" :key="slug" :value="slug">
            {{ DELIVERY_PLATFORM_LABELS[slug] }}
          </SelectItem>
        </SelectContent>
      </Select>

      <Select v-if="mappedStores.length > 1" v-model="storeId">
        <SelectTrigger class="w-56" :aria-label="t('pages.deliveryReviews.reviews.filters.store')">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem :value="ALL">{{ t('pages.deliveryReviews.reviews.filters.allStores') }}</SelectItem>
          <SelectItem v-for="store in mappedStores" :key="store.id" :value="String(store.id)">
            {{ store.name }}
          </SelectItem>
        </SelectContent>
      </Select>

      <Select v-model="rating">
        <SelectTrigger class="w-40" :aria-label="t('pages.deliveryReviews.reviews.filters.rating')">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem :value="ALL">{{ t('pages.deliveryReviews.reviews.filters.allRatings') }}</SelectItem>
          <SelectItem v-for="stars in [5, 4, 3, 2, 1]" :key="stars" :value="String(stars)">
            {{ t('pages.deliveryReviews.reviews.filters.stars', { count: stars }, stars) }}
          </SelectItem>
        </SelectContent>
      </Select>

      <Label class="flex h-9 items-center gap-2 text-sm">
        <Switch v-model="onlyUnanswered" />
        {{ t('pages.deliveryReviews.reviews.filters.onlyUnanswered') }}
      </Label>

      <p class="ml-auto text-sm text-muted-foreground">
        {{ t('pages.deliveryReviews.reviews.count', { count: total }, total) }}
      </p>
    </div>

    <AppSkeleton v-if="isLoading && reviews.length === 0" class="h-40 w-full" />
    <BaseTip v-else-if="hasError" severity="error" :text="t('pages.deliveryReviews.reviews.loadError')" />
    <Card v-else-if="reviews.length === 0" class="p-8 text-center text-sm text-muted-foreground">
      {{ t('pages.deliveryReviews.reviews.empty') }}
    </Card>
    <template v-else>
      <DeliveryReviewCard
        v-for="review in reviews"
        :key="review.id"
        :merchant-id="merchantId"
        :review="review"
        @replied="onReplied"
      />

      <div v-if="lastPage > 1" class="flex items-center justify-center gap-3">
        <Button
          variant="outline"
          size="icon-sm"
          :disabled="page <= 1 || isLoading"
          :aria-label="t('pages.deliveryReviews.reviews.previousPage')"
          @click="load(page - 1)"
        >
          <ChevronLeft />
        </Button>
        <span class="text-sm text-muted-foreground">{{ page }} / {{ lastPage }}</span>
        <Button
          variant="outline"
          size="icon-sm"
          :disabled="page >= lastPage || isLoading"
          :aria-label="t('pages.deliveryReviews.reviews.nextPage')"
          @click="load(page + 1)"
        >
          <ChevronRight />
        </Button>
      </div>
    </template>
  </div>
</template>
